import React from 'react';
import { cx } from './clases';

/** Partículas que no cuentan para las iniciales («María de la Luz» → M, L). */
const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'da', 'das', 'do', 'dos', 'van', 'von', 'mc']);

/**
 * Iniciales como en el diseño: nombre + primer apellido.
 *
 *   «Alexis Uriel Garcia Dominguez» → AG   (dos nombres y dos apellidos: el penúltimo)
 *   «Hector Bonilla Castillo»       → HB   (un nombre y dos apellidos: el segundo)
 *   «PAULINA RÍOS»                  → PR
 *   «Cher»                          → CH
 *
 * Con tres palabras no se sabe si son dos nombres y un apellido o al revés;
 * se toma la segunda, que es lo más común en la plantilla de BUK.
 */
export function iniciales(nombre: string | null | undefined): string {
  const palabras = (nombre ?? '')
    .trim()
    .split(/\s+/)
    .filter((p) => p && !PARTICULAS.has(p.toLowerCase()));
  if (!palabras.length) return '?';
  if (palabras.length === 1) return palabras[0].slice(0, 2).toLocaleUpperCase('es-MX');
  const segunda = palabras.length >= 4 ? palabras[palabras.length - 2] : palabras[1];
  return `${palabras[0][0]}${segunda[0]}`.toLocaleUpperCase('es-MX');
}

interface Props {
  nombre: string | null | undefined;
  /** Diámetro en px (36 por omisión). */
  tamano?: number;
  className?: string;
}

/**
 * Círculo con las iniciales. Es decorativo (`aria-hidden`): el nombre completo
 * siempre va escrito a su lado.
 */
export const Avatar: React.FC<Props> = ({ nombre, tamano = 36, className }) => (
  <span
    aria-hidden="true"
    className={cx(
      'flex shrink-0 items-center justify-center rounded-full bg-metas-avatar font-semibold text-metas-avatar-texto',
      className,
    )}
    style={{ width: tamano, height: tamano, fontSize: Math.round(tamano * 0.36) }}
    data-testid="avatar"
  >
    {iniciales(nombre)}
  </span>
);

export default Avatar;
